import React, { useState, useRef, useEffect } from "react";
import { Search } from "@mui/icons-material";
import CalendorIcon from "../../../../src/assests/images/calendar-2.svg";
import sorterArrow from "../../../../src/assests/images/SortingArrow.svg";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import {
  Box,
  Link,
  Typography,
  TextField,
  InputAdornment,
  Button,
  ListItemText,
  ListItemButton,
  List,
} from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { debounce } from "lodash";
import LoginOtpRep from "./ReportsLogTable";
import ReportsLogFilter from "./ReportsLogFilter";

// use the SAME arrow assets used in BulkUploads
import arrowLeft from "../../../assests/images/arrow-left.svg";
import arrowRight from "../../../assests/images/arrow-right.png";
import doubleArrowLeft from "../../../assests/images/double-arrow-left.svg";
import doubleArrowRight from "../../../assests/images/double-arrow-right.svg";

// UPDATED: supports `onClear`
const CustomInput = React.forwardRef<HTMLInputElement, any>(
  ({ value, onClick, onClear }, ref) => (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        border: "1px solid #ccc",
        backgroundColor: "#fff",
        borderRadius: "10px",
        padding: "0 10px",
        height: "48px",
        width: "301px",
        cursor: "pointer",
      }}
    >
      <input
        ref={ref}
        value={value || "Select Date Range (Last Login)"}
        style={{
          fontSize: "14px",
          border: "none",
          outline: "none",
          flex: 1,
          color: "#000",
          backgroundColor: "transparent",
        }}
        readOnly
        onClick={onClick}
      />
      {value && (
        <img
          onClick={(e) => {
            e.stopPropagation();
            onClear?.();
          }}
          src={
            "data:image/svg+xml;utf8," +
            encodeURIComponent(
              `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path fill="#888" d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59L7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.42L12 13.41l4.89 4.9a1 1 0 0 0 1.42-1.42L13.41 12l4.9-4.89a1 1 0 0 0-.01-1.4Z"/></svg>`
            )
          }
          alt="Clear"
          style={{ height: 18, width: 18, cursor: "pointer", marginRight: 8 }}
        />
      )}
      <img
        src={CalendorIcon}
        alt="Calendar Icon"
        style={{ height: "20px", width: "20px", cursor: "pointer" }}
        onClick={onClick}
      />
    </div>
  )
);

function ReportsLogin() {
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  // ⬇️ CHANGED: use a number (bottom y) like in RewardsPointsBook
  const [popupPosition, setPopupPosition] = useState<number>(0);

  const sortButtonRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [startDate, endDate] = dateRange;
  const [sortOption, setSortOption] = useState<"highest" | "lowest" | null>(null);

  // ✅ filter modal state
  const [shouldFetchFilters, setShouldFetchFilters] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<any>(null);

  // ✅ NEW: keep an inline "lastLogin" only when both dates are selected (YYYY-MM-DD)
  const [inlineLastLogin, setInlineLastLogin] = useState<{ from: string | null; to: string | null } | null>(null);

  const handleSortButtonClick = () => setIsPopupOpen(!isPopupOpen);

  const openFilterPopup = () => {
    setShouldFetchFilters(true);
    setIsFilterOpen(true);
  };
  const handleFilterPopupFetched = () => {
    setShouldFetchFilters(false);
  };

  // ✅ debounce search
  useEffect(() => {
    const handleDebounce = debounce((query: string) => setDebouncedSearch(query), 300);
    handleDebounce(searchQuery);
    return () => handleDebounce.cancel();
  }, [searchQuery]);

  // ✅ calculate popup position on open (match RewardsPointsBook behavior)
  useEffect(() => {
    if (sortButtonRef.current && isPopupOpen) {
      const rect = sortButtonRef.current.getBoundingClientRect();
      setPopupPosition(rect.bottom);
    }
  }, [isPopupOpen]);

  // ✅ close popup on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        !sortButtonRef.current?.contains(event.target as Node)
      ) {
        setIsPopupOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close on Escape (parity with Rewards)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsFilterOpen(false);
        setIsPopupOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleSortSelection = (option: "highest" | "lowest") => {
    setSortOption(option);
    setIsPopupOpen(false);
  };

  // ✅ helper to format as YYYY-MM-DD (en-CA)
  const toYMD = (d: Date) => d.toLocaleDateString("en-CA");

  // ✅ commit inline range when both dates picked
  const handleDateChange = (update: [Date | null, Date | null] | null) => {
    if (!Array.isArray(update)) return;
    const [s, e] = update;
    setDateRange([s, e]);

    if (s && e) {
      setInlineLastLogin({ from: toYMD(s), to: toYMD(e) });
    } else {
      setInlineLastLogin(null);
    }
  };

  const handleCalendarClose = () => {
    if (!(startDate && endDate)) {
      setInlineLastLogin(null);
    }
  };

  // ✅ inline dates (when complete) override modal lastLogin
  const computedFilters = React.useMemo(() => {
    const base = appliedFilters ? { ...appliedFilters } : {};
    if (inlineLastLogin) {
      (base as any).lastLogin = { ...inlineLastLogin };
    }
    return base;
  }, [appliedFilters, inlineLastLogin]);

  // ✅ lightweight key so the table/effects always notice changes
  const filterKey = React.useMemo(() => {
    const lastLogin = (computedFilters as any)?.lastLogin || null;
    return JSON.stringify(lastLogin);
  }, [computedFilters]);

  return (
    <Box sx={{ background: "#fff", paddingTop: "30px" }}>
      {/* ───────────── Header Row: Title (left) + Date Range (right) ───────────── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 32px",
        }}
      >
        <Box>
          <Link
            href="#"
            sx={{
              textDecoration: "none",
              fontSize: "14px",
              fontWeight: "500",
              color: "#696A6B",
              paddingRight: "10px",
            }}
          >
            Login & OTP Reports
          </Link>
          <Typography
            variant="h3"
            sx={{
              fontSize: "24px",
              fontWeight: "600",
              color: "#000",
              marginTop: "16px",
            }}
          >
            Login & OTP Reports
          </Typography>
        </Box>

        {/* ⬆️ Date Range moved here (was Filter before) */}
        {/* <Box sx={{ display: "flex", alignItems: "center" }}>
          <DatePicker
            selectsRange
            startDate={startDate || undefined}
            endDate={endDate || undefined}
            onChange={handleDateChange}
            onCalendarClose={handleCalendarClose}
            customInput={
              <CustomInput
                onClear={() => {
                  setDateRange([null, null]);
                  setInlineLastLogin(null);
                  setAppliedFilters((prev: any) =>
                    prev ? { ...prev, lastLogin: { from: null, to: null } } : prev
                  );
                }}
              />
            }
            renderCustomHeader={({
              date,
              decreaseMonth,
              increaseMonth,
              decreaseYear,
              increaseYear,
            }) => (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <button onClick={decreaseYear} aria-label="Previous year">
                  <img src={doubleArrowLeft} alt="Previous year" />
                </button>
                <button onClick={decreaseMonth} aria-label="Previous month">
                  <img src={arrowLeft} alt="Previous month" />
                </button>
                <span style={{ margin: "0 10px" }}>
                  {date.toLocaleString("default", { year: "numeric", month: "long" })}
                </span>
                <button onClick={increaseMonth} aria-label="Next month">
                  <img src={arrowRight} alt="Next month" />
                </button>
                <button onClick={increaseYear} aria-label="Next year">
                  <img src={doubleArrowRight} alt="Next year" />
                </button>
              </div>
            )}
          />
        </Box> */}
      </Box>

      {/* ───────────── Search Row: Search (left) + Filter button (right) ───────────── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "16px",
          padding: "0 32px 30px",
        }}
      >
        {/* Search */}
        <Box sx={{ maxWidth: "520px", flexGrow: 1 }}>
          <TextField
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by User ID, PMI Entity Code, User Name,Outlet Name,City,Outlet Category, Locations"
            type="search"
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ height: "20px", width: "20px" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                "& fieldset": { border: "2px solid #F5F5F5" },
                height: "48px",
              },
            }}
          />
        </Box>

        {/* ⬇️ Filter moved here (was Date Range before) */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            onClick={openFilterPopup}
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              bgcolor: "#C8172F",
              color: "#fff",
              borderColor: "#C8172F",
              borderRadius: "8px",
              height: "48px",
              padding: "0px 16px",
              "&:hover": { bgcolor: "#a31425", borderColor: "#a31425" },
            }}
          >
            Filter
          </Button>
        </Box>
      </Box>

      {/* Sort Popup (unchanged/optional) */}
      {isPopupOpen && (
        <Box
          ref={popupRef}
          sx={{
            position: "absolute",
            top: `${popupPosition + 10}px`,
            right: "30px",
            backgroundColor: "#fff",
            border: "1px solid #ccc",
            borderRadius: "8px",
            padding: "10px 0",
            zIndex: 999,
            boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
            width: "320px",
          }}
        >
          <List>
            {[
              { label: "Highest to Lowest Points", value: "highest" },
              { label: "Lowest to Highest Points", value: "lowest" },
            ].map(({ label, value }) => (
              <ListItemButton
                key={value}
                onClick={() => {
                  setSortOption(value as "highest" | "lowest");
                  setIsPopupOpen(false);
                }}
              >
                <ListItemText primary={label} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      )}

      {/* Table */}
      <Box
        sx={{
          width: "100%",
          background: "#f9fbfc",
          borderTop: "1px solid #d3d3d3",
        }}
      >
        <LoginOtpRep
          key={filterKey} // force refresh on range change
          searchQuery={debouncedSearch}
          dateRange={dateRange}
          appliedFilters={computedFilters}
          // sortOption={sortOption}
        />
      </Box>

      {/* Filter Modal */}
      {isFilterOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0,0,0,0.35)",
            zIndex: 2000,
            padding: 16,
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setIsFilterOpen(false);
          }}
        >
          <div style={{ display: "flex", justifyContent: "center", width: "auto" }}>
            <ReportsLogFilter
              isOpen={isFilterOpen}
              shouldFetch={shouldFetchFilters}
              onFetched={handleFilterPopupFetched}
              onApplyFilters={(payload: any) => {
                setAppliedFilters(payload);
                setIsFilterOpen(false);
              }}
              onCancel={() => setIsFilterOpen(false)}
              dateRange={[startDate, endDate]}
              setDateRange={(r: any) => setDateRange(r)}
              initialFilters={appliedFilters}
            />
          </div>
        </div>
      )}

      {/* Bottom bar (unchanged) */}
      <Box
        sx={{
          mt: 6,
          px: 4,
          py: 2,
          borderTop: "1px solid #eee",
          backgroundColor: "#fff",
          position:"fixed",
          bottom:"0",
          width:"100%"
        }}
      >
        <Button
          variant="text"
          startIcon={<ArrowBackIosNewIcon fontSize="small" />}
          onClick={() => window.history.back()}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            color: "#C8172F",
            "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
          }}
        >
          Back
        </Button>
      </Box>
    </Box>
  );
}

export default ReportsLogin;
